/**
 * usePlatformVoice — real-time read/save for the canonical PlatformVoice doc.
 *
 * Firestore path (single nested doc — matches canonical schema 1.3.1+):
 *   projects/{pid}/authors/{authorId}/platforms/{platformId}/voice/voice
 *
 * NOT the platform doc itself (`platforms/{platformId}`) — that one carries
 * platform config (maxPostLength, optimalTimes, etc.); voice fields live in
 * a child document. AuthorOnboardingHandler.persistOnboardingBundle writes
 * to `platformRef.collection('voice').doc('voice')`.
 *
 * Returns the current PlatformVoice document, a `dirtyFields` set computed
 * via the L.5 lastExtractedFields snapshot, and a `save(patch)` mutator.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '../utils/stripUndefined';
import type { PlatformVoice } from '../types';
import type { AuthorBasePath } from '../services/AuthorPlatformService';

export interface UsePlatformVoiceReturn {
  voice: PlatformVoice | null;
  /** L.5 — keys that diverge from `lastExtractedFields`. Used by VoiceDiffPanel
   *  to colour-code rows: clean vs dirty. */
  dirtyFields: Set<string>;
  loading: boolean;
  error: Error | null;
  save: (patch: Partial<PlatformVoice>) => Promise<void>;
}

const VOICE_FIELD_KEYS: Array<keyof PlatformVoice> = [
  'voice',
  'toneVariations',
  'vocabulary',
  'avoidPatterns',
  'structurePreferences',
  'emojiPolicy',
  'hashtagPolicy',
  'platformNotes',
];

function deepEqualJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

export function usePlatformVoice(
  projectId: string | null | undefined,
  authorId: string | null | undefined,
  platformId: string | null | undefined,
  basePath: AuthorBasePath = 'authors',
): UsePlatformVoiceReturn {
  const [voice, setVoice] = useState<PlatformVoice | null>(null);
  const [loading, setLoading] = useState<boolean>(
    Boolean(projectId && authorId && platformId),
  );
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !authorId || !platformId) {
      setVoice(null);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = doc(
      db,
      'projects',
      projectId,
      basePath,
      authorId,
      'platforms',
      platformId,
      'voice',
      'voice',
    );
    setLoading(true);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setVoice(null);
        } else {
          const data = snap.data() as Partial<PlatformVoice>;
          setVoice({ ...(data as PlatformVoice), authorId, platformId });
        }
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, authorId, platformId, basePath]);

  const dirtyFields = useMemo(() => {
    const dirty = new Set<string>();
    if (!voice?.lastExtractedFields) return dirty;
    for (const k of VOICE_FIELD_KEYS) {
      const current = (voice as any)[k];
      const snapshot = (voice.lastExtractedFields as any)[k];
      if (!deepEqualJson(current, snapshot)) dirty.add(k as string);
    }
    return dirty;
  }, [voice]);

  const save = useCallback(
    async (patch: Partial<PlatformVoice>) => {
      if (!projectId || !authorId || !platformId) return;
      const db = CoreFirebaseService.getInstance().firestore;
      const ref = doc(
        db,
        'projects',
        projectId,
        basePath,
        authorId,
        'platforms',
        platformId,
      );
      const stripped = stripUndefined({
        ...patch,
        authorId,
        platformId,
        updatedAt: serverTimestamp() as any,
      }) as Record<string, unknown>;
      await setDoc(ref, stripped, { merge: true });
    },
    [projectId, authorId, platformId, basePath],
  );

  return { voice, dirtyFields, loading, error, save };
}
